/**
 * adultAutismStages.js
 *
 * Single source of truth for Adult Autism assessment stage metadata on the
 * frontend. Centralises the mapping between Firestore document IDs (the
 * "stage keys" used in assessmentStages and reportContent subcollections),
 * their abbreviated report-field prefixes, and the short keys used in
 * patient_summaries.progressByStage for dashboard pills.
 *
 * Why this file exists:
 *   Before this, the stage-to-prefix map was duplicated in
 *   DocumentsDashboard_Autism and ad-hoc strings were scattered across
 *   gating/progress logic. Centralising prevents drift when new stages
 *   are added (e.g. Phase 1 OBS, Phase 2 DD) and gives a single import
 *   for both UI and utility code.
 *
 * Must stay aligned with:
 *   - python-functions/functions/autism_patient_builder.py
 *     (ASSESSMENT_STAGE_KEYS)
 *   - python-functions/functions/autism_stage_progress.py
 *     (STAGE_TO_PROGRESS_KEY, REPORT_APPROVAL_FIELDS)
 */

// ─── Stage keys ─────────────────────────────────────────────────────────────
// Canonical Firestore document IDs used in both assessmentStages and
// reportContent subcollections. Order matches the clinical workflow.

export const AUTISM_STAGE_KEYS = [
  'history',
  'semiStructuredInterview',
  'observation',
  'diagnosticDecision',
  'postAssessmentDiscussion',
];

// ─── Optional-stage helper ──────────────────────────────────────────────────
// Behavioural Observation is an OPTIONAL stage: some orgs (e.g. Lancaster
// Medical Practice) do not run a separate Observation appointment, so their
// Adult Autism workflow skips it entirely. Rather than sprinkle
// `if (isObservationEnabled)` / org-name checks across every file that iterates
// the stage list, callers derive the stages that actually apply to a patient
// from this ONE helper. Keeping a single source of truth means adding/removing
// an optional stage in future is a one-line change here.
//
// IMPORTANT: this only affects which stages are *active* for gating, progress,
// documents, reporting, etc. The Firestore skeleton documents for every stage
// (including `observation`) are still created for all autism patients — the
// stage is hidden/ignored, not deleted — so re-enabling Observation for an org
// later is a config flip with no data migration.

/**
 * Stage keys that are always present regardless of workflow config. Only
 * `observation` is currently optional.
 */
const OPTIONAL_AUTISM_STAGE_KEYS = ['observation'];

/**
 * Return the ordered autism stage keys that apply to a patient given their
 * resolved workflow config.
 *
 * @param {Object} [wf] - Resolved workflow config (from useWorkflowConfig /
 *   get_workflow_config). We read `wf.isObservationEnabled`; when it is
 *   explicitly `false` the `observation` stage is dropped. Absent/undefined
 *   defaults to enabled, so TOC and every existing autism patient keep the
 *   full 5-stage list unchanged.
 * @returns {string[]} Subset of AUTISM_STAGE_KEYS in canonical order.
 */
export function getEnabledAutismStages(wf) {
  const isObservationEnabled = wf?.isObservationEnabled !== false;
  if (isObservationEnabled) return [...AUTISM_STAGE_KEYS];
  return AUTISM_STAGE_KEYS.filter(
    (stageKey) => !OPTIONAL_AUTISM_STAGE_KEYS.includes(stageKey),
  );
}

/**
 * Convenience predicate: is a given stage active for this patient's workflow?
 *
 * @param {string} stageKey - One of AUTISM_STAGE_KEYS
 * @param {Object} [wf]     - Resolved workflow config (see getEnabledAutismStages)
 * @returns {boolean}
 */
export function isAutismStageEnabled(stageKey, wf) {
  return getEnabledAutismStages(wf).includes(stageKey);
}

// ─── Report-field prefix map ────────────────────────────────────────────────
// Maps each stage key to the abbreviated prefix used in Firestore field names
// for report-level flags. For example, the history stage stores its approval
// flag as `historyReportApproved` (prefix "history"), while SSI stores
// `ssiReportApproved` (prefix "ssi").
//
// These prefixes match the Python Cloud Functions that generate and approve
// reports, and mirror the ADHD convention (ci/ssi/dd/pad).

export const STAGE_TO_REPORT_PREFIX = {
  history: 'history',
  semiStructuredInterview: 'ssi',
  observation: 'obs',
  diagnosticDecision: 'dd',
  postAssessmentDiscussion: 'pad',
};

// ─── Dashboard progress key map ─────────────────────────────────────────────
// Maps stage keys to the short keys stored in patient_summaries.progressByStage
// and displayed as dashboard pills in PatientTable. History and SSI are combined
// into a single "ddi" (Diagnostic Developmental Interview) indicator because
// they are conducted together in one appointment.

export const STAGE_TO_PROGRESS_KEY = {
  history: 'ddi',
  semiStructuredInterview: 'ddi',
  observation: 'obs',
  diagnosticDecision: 'dd',
  postAssessmentDiscussion: 'pad',
};

// ─── Patient Overview module → stage keys ───────────────────────────────────
// The Adult Autism Patient Overview shows ONE card per appointment, not one per
// Firestore stage. History and the SSI are run back-to-back in a single
// "Diagnostic Developmental Interview" sitting, so they share a card
// (`developmentalDiagnosticInterview`) even though each remains a distinct
// stage behind the scenes with its own reportContent document, its own CAR
// and its own Patient Report appendix.
//
// This map is the single place that grouping is declared. Anything that must
// walk "the stages behind this card" (appointment dates, inferred-date
// anchors, report re-finalisation) should read from here rather than hard-code
// the pairing. Array order matters: the FIRST stage is the card's primary
// stage, used when a single value is needed (e.g. which stage's timestamp to
// show as the estimated appointment date — History first, SSI as fallback).

export const OVERVIEW_MODULE_TO_STAGE_KEYS = {
  developmentalDiagnosticInterview: ['history', 'semiStructuredInterview'],
  behaviouralObservation: ['observation'],
  diagnosticDecision: ['diagnosticDecision'],
  postAssessmentDiscussion: ['postAssessmentDiscussion'],
};

/**
 * Short key under `patients/{id}.appointmentDates` for an Autism overview
 * module (card). Returns `ddi`, `obs`, `dd` or `pad`, or null for an unknown
 * module key.
 *
 * Derived from STAGE_TO_PROGRESS_KEY of the module's first stage so the key a
 * clinician's appointment date is stored under is the very same string the
 * dashboard pill and the Python PDF pipeline (`autism_stage_progress.py`)
 * already use. No separate "appointment key" vocabulary to keep in sync.
 *
 * @param {string} moduleKey - A key of OVERVIEW_MODULE_TO_STAGE_KEYS
 * @returns {string|null}
 */
export function getAutismAppointmentShortKey(moduleKey) {
  const stageKeys = OVERVIEW_MODULE_TO_STAGE_KEYS[moduleKey];
  if (!stageKeys || stageKeys.length === 0) return null;
  return STAGE_TO_PROGRESS_KEY[stageKeys[0]] || null;
}

// ─── Clinician role map ─────────────────────────────────────────────────────
// Maps each stage key to the clinician role field on the patient header
// document under `clinicians`. Used by DocumentsDashboard_Autism to show
// who is assigned to review each stage's report outputs.

export const STAGE_TO_CLINICIAN_KEY = {
  history: 'clinicianCI',
  semiStructuredInterview: 'clinicianSSI',
  observation: 'clinicianOBS',
  diagnosticDecision: 'clinicianDD',
  postAssessmentDiscussion: 'clinicianPAD',
};

// ─── Flag-name helpers ──────────────────────────────────────────────────────
// Compose the exact Firestore field names for report approval and generation
// flags from a stage key. Avoids string concatenation scattered across files.

/**
 * Returns the Firestore field name for the report-approved flag on a
 * reportContent document. e.g. 'history' → 'historyReportApproved'.
 *
 * @param {string} stageKey - One of AUTISM_STAGE_KEYS
 * @returns {string}
 */
export function getReportApprovedField(stageKey) {
  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  if (!prefix) throw new Error(`Unknown autism stage key: ${stageKey}`);
  return `${prefix}ReportApproved`;
}

/**
 * Returns the Firestore field name for the report-generated flag on a
 * reportContent document. e.g. 'semiStructuredInterview' → 'ssiReportGenerated'.
 *
 * @param {string} stageKey - One of AUTISM_STAGE_KEYS
 * @returns {string}
 */
export function getReportGeneratedField(stageKey) {
  const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
  if (!prefix) throw new Error(`Unknown autism stage key: ${stageKey}`);
  return `${prefix}ReportGenerated`;
}
